import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Department, DepartmentListFilters, DepartmentsResponse } from '../models/departments.model';

@Injectable({ providedIn: 'root' })
export class DepartmentsService {
  private readonly baseUrl = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  getDepartments(
    pageNumber: number = 1,
    pageSize: number = 10,
    orderBy: string = 'codeAsc',
    filters?: DepartmentListFilters,
  ): Observable<DepartmentsResponse | null> {
    let params = new HttpParams()
      .set('pageNumber', String(pageNumber))
      .set('pageSize', String(pageSize))
      .set('orderBy', orderBy);

    const searchTerm = String(filters?.searchTerm ?? '').trim();
    if (searchTerm) {
      params = params.set('searchTerm', searchTerm);
    }

    return this.http.get<DepartmentsResponse>(`${this.baseUrl}/api/departments`, { params }).pipe(
      catchError((err) => {
        console.error('Error fetching departments:', err);
        return of(null);
      }),
    );
  }

  getDepartmentById(id: number): Observable<Department | null> {
    return this.http
      .get<{ status: string; data: Department }>(`${this.baseUrl}/api/departments/${id}`)
      .pipe(
        map((res) => res.data),
        catchError((err) => {
          console.error('Error fetching department by ID:', err);
          return of(null);
        }),
      );
  }

  getDepartmentTree(): Observable<Department[] | null> {
    return this.http
      .get<{
        status: string;
        data: Department[];
      }>(`${this.baseUrl}/api/departments/department-tree`)
      .pipe(
        map((res) => res.data),
        catchError((err) => {
          console.error('Error fetching department tree:', err);
          return of(null);
        }),
      );
  }

  createDepartment(payload: {
    name: string;
    code: string;
    parentDepartmentId?: number | null;
  }): Observable<{ success: boolean; message: string }> {
    return this.http.post<{ status?: string; message?: string }>(`${this.baseUrl}/api/departments`, payload).pipe(
      map((res) => ({
        success: true,
        message: res?.message || 'Department created successfully',
      })),
      catchError((err) => {
        console.error('Error creating department:', err);

        return of({
          success: false,
          message:
            err?.error?.message ||
            err?.error?.title ||
            err?.message ||
            'Failed to create department',
        });
      }),
    );
  }

  updateDepartment(
    id: number,
    payload: { name?: string; code?: string; parentDepartmentId?: number | null },
  ): Observable<{ success: boolean; message: string }> {
    return this.http
      .patch<{ status?: string; message?: string }>(`${this.baseUrl}/api/departments/${id}`, payload)
      .pipe(
        map((res) => ({
          success: true,
          message: res?.message || 'Department updated successfully',
        })),
        catchError((err) => {
          console.error('Error updating department:', err);

          return of({
            success: false,
            message:
              err?.error?.message ||
              err?.error?.title ||
              err?.message ||
              'Failed to update department',
          });
        }),
      );
  }

  deleteDepartment(id: number): Observable<{ success: boolean; message: string }> {
    return this.http.delete<{ status?: string; message?: string }>(`${this.baseUrl}/api/departments/${id}`).pipe(
      map((res) => ({
        success: true,
        message: res?.message || 'Department deleted successfully',
      })),
      catchError((err) => {
        console.error('Error deleting department:', err);

        return of({
          success: false,
          message:
            err?.error?.message ||
            err?.error?.title ||
            err?.message ||
            'Failed to delete department',
        });
      }),
    );
  }
}